import { useCallback, useEffect, useMemo, useState } from 'react'
import { FaBroom, FaChevronDown, FaChevronUp, FaLeaf, FaMapMarkerAlt, FaPaintBrush, FaRegClock, FaSearch, FaShieldAlt, FaSpa, FaUserCheck } from 'react-icons/fa'
import { useCategories, useProviderFeed, useDistricts } from '@/hooks/useServices'
import { useLocation } from '@/hooks/useLocation'
import useModalStore from '@/stores/ModalStore'
import ActiveServiceBanner from '@/components/common/ActiveServiceBanner/ActiveServiceBanner'
import LocationSetup from '@/modals/LocationSetup/LocationSetup'
import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import ProviderCard from '@/components/cards/ProviderCard/ProviderCard'
import InputAdvanced from '@/components/forms/InputAdvanced/InputAdvanced'
import toast from 'react-hot-toast'
import { buildUserLocationFromPlace, findDistrictByName } from '@/utils/districts'
import type { DistrictRecord } from '@/utils/districts'
import './Search.css'

type PricingFilter = 'all' | 'hourly' | 'quote'

const quickPrompts = [
    {
        id: 'cleaning',
        title: 'Limpieza de hogar',
        caption: 'Desde S/ 45 por hora',
        icon: <FaBroom />,
        query: 'Limpieza',
    },
    {
        id: 'garden',
        title: 'Servicio de jardinería',
        caption: 'Cuidamos tus áreas verdes',
        icon: <FaLeaf />,
        query: 'Jardinería',
    },
    {
        id: 'lavacarro',
        title: 'Lavado de carro',
        caption: 'Cotiza tu proyecto',
        icon: <FaPaintBrush />,
        query: 'Lavado de carro',
    },
    {
        id: 'spa',
        title: 'Masajes a domicilio',
        caption: 'Relájate sin salir de casa',
        icon: <FaSpa />,
        query: 'Masaje',
    },
]

const ServiceSearch = () => {
    const { data: categories } = useCategories()
    const { location, setManualLocation } = useLocation()
    const { setModalContent, closeModal } = useModalStore()

    const { data: districtsData } = useDistricts()
    const districts = useMemo(() => Array.isArray(districtsData) ? districtsData as DistrictRecord[] : [], [districtsData])

    const [searchQuery, setSearchQuery] = useState('')
    const [categoryIds, setCategoryIds] = useState<number[]>([])
    const [pricingFilters, setPricingFilters] = useState<PricingFilter[]>([])
    const [isCategoryExpanded, setIsCategoryExpanded] = useState(false)
    const [isPricingExpanded, setIsPricingExpanded] = useState(false)
    const [addressValue, setAddressValue] = useState('')
    const [addressError, setAddressError] = useState<string | null>(null)
    const [districtPage, setDistrictPage] = useState(1)
    const [allProvidersPage, setAllProvidersPage] = useState(1)

    useEffect(() => {
        if (location) {
            setAddressValue(location.address || location.districtName || '')
            setAddressError(null)
        }
    }, [location])

    // Reset pagination when filters change
    useEffect(() => {
        setDistrictPage(1)
        setAllProvidersPage(1)
    }, [location?.districtId, categoryIds, searchQuery])

    // Fetch district-specific providers (when district is selected)
    const { data: districtFeed, isLoading: isLoadingDistrict, isError: isErrorDistrict, refetch: refetchDistrict } = useProviderFeed({
        districtId: location?.districtId || undefined,
        categoryId: categoryIds.length > 0 ? categoryIds[0] : undefined,
        searchQuery,
        page: districtPage,
        pageSize: 20,
        allProviders: false,
    }, {
        enabled: !!location?.districtId, // Only fetch when district is selected
    })

    // Fetch all providers (when no district OR for "Other Providers" section)
    const { data: allProvidersFeed, isLoading: isLoadingAll, isError: isErrorAll, refetch: refetchAll } = useProviderFeed({
        districtId: location?.districtId || undefined,
        categoryId: categoryIds.length > 0 ? categoryIds[0] : undefined,
        searchQuery,
        page: allProvidersPage,
        pageSize: 20,
        allProviders: true,
    })

    const districtProviders = useMemo(() => {
        return Array.isArray(districtFeed?.providers) ? districtFeed.providers : []
    }, [districtFeed])

    const allProviders = useMemo(() => {
        return Array.isArray(allProvidersFeed?.providers) ? allProvidersFeed.providers : []
    }, [allProvidersFeed])

    const isLoading = isLoadingDistrict || isLoadingAll
    const isError = isErrorDistrict || isErrorAll

    const refetch = useCallback(() => {
        refetchDistrict()
        refetchAll()
    }, [refetchDistrict, refetchAll])

    const filterProviders = useCallback((providerList: any[]) => {
        let result = providerList

        // Filter by pricing types
        if (pricingFilters.length > 0 && !pricingFilters.includes('all')) {
            result = result.filter((provider: any) =>
                (provider.service_offerings || []).some(
                    (offering: any) => pricingFilters.includes(offering.pricing_type)
                )
            )
        }

        // Client-side search is handled by backend, but we can refine if needed
        return result
    }, [pricingFilters])

    const filteredDistrictProviders = useMemo(() => {
        return filterProviders(districtProviders)
    }, [districtProviders, filterProviders])

    const filteredAllProviders = useMemo(() => {
        return filterProviders(allProviders)
    }, [allProviders, filterProviders])

    const openLocationSetup = useCallback(() => {
        setModalContent(
            <LocationSetup
                handleClose={() => {
                    closeModal()
                    refetch()
                }}
            />
        )
    }, [closeModal, refetch, setModalContent])

    const handleQuickPrompt = (prompt: (typeof quickPrompts)[number]) => {
        setSearchQuery(prompt.query)
        setPricingFilters([])
        setCategoryIds([])
        toast.success(`Mostrando resultados para "${prompt.query}"`)
    }

    const handleCategoryToggle = useCallback((categoryId: number | null) => {
        if (categoryId === null) {
            // "Todas" selected - clear all
            setCategoryIds([])
        } else {
            setCategoryIds((prev) => {
                if (prev.includes(categoryId)) {
                    // Deselect
                    return prev.filter((id) => id !== categoryId)
                } else {
                    // Select - add to array
                    return [...prev, categoryId]
                }
            })
        }
    }, [])

    const handlePricingToggle = useCallback((filter: PricingFilter) => {
        if (filter === 'all') {
            // "Todos" selected - clear all others
            setPricingFilters([])
        } else {
            setPricingFilters((prev) => {
                if (prev.includes(filter)) {
                    // Deselect
                    return prev.filter((f) => f !== filter)
                } else {
                    // Select - add to array, remove 'all' if present
                    return [...prev.filter((f) => f !== 'all'), filter]
                }
            })
        }
    }, [])

    const resetFilters = useCallback(() => {
        setCategoryIds([])
        setPricingFilters([])
        setSearchQuery('')
        toast.success('Filtros restablecidos')
    }, [])

    const handleAddressInputChange = useCallback((_: any, value: string) => {
        setAddressValue(value)
        setAddressError(null)
    }, [])

    const handleManualDistrict = useCallback(async (value: string) => {
        const trimmed = value.trim()
        if (!trimmed) {
            setAddressError('Escribe la dirección o el nombre del distrito')
            return
        }

        const match = findDistrictByName(districts, trimmed)
        if (!match) {
            setAddressError('No pudimos reconocer el distrito. Selecciónalo manualmente.')
            toast.error('Selecciona un distrito válido de Lima Metropolitana.')
            return
        }

        await setManualLocation({
            districtId: match.id,
            districtName: match.name,
            address: trimmed,
            label: `Distrito ${match.name}`,
            city: 'Lima',
        })
        setAddressError(null)
        toast.success(`Mostrando servicios en ${match.name}`)
        refetch()
    }, [districts, refetch, setManualLocation])

    const handlePlaceSelected = useCallback(async (place: any) => {
        const parsed = buildUserLocationFromPlace(place, districts, location || {})
        if (!parsed) {
            setAddressError('No pudimos reconocer el distrito. Selecciónalo manualmente.')
            toast.error('Selecciona una dirección dentro de Lima Metropolitana.')
            return
        }

        await setManualLocation(parsed)
        setAddressError(null)
        toast.success(`Usaremos servicios para ${parsed.districtName}`)
        refetch()
    }, [districts, location, setManualLocation, refetch])

    const showLocationWarning = !location?.districtId

    return (
        <div className="search-page">
            <ActiveServiceBanner />

            <section className="search-hero">
                <div className="search-hero-inner">
                    <div className="search-hero-content">
                        <div className="search-hero-copy">
                            {/* <span className="search-hero-kicker">Encuentra ayuda rápida</span> */}
                            <h1>Servicios confiables en Lima Metropolitana</h1>
                            <p>
                                Selecciona tu distrito, filtra por categoría y contacta Domber Amigos verificados con disponibilidad inmediata.
                            </p>
                            <div className="search-hero-benefits">
                                <div className="search-hero-benefit">
                                    <span className="search-hero-benefit-icon">
                                        <FaShieldAlt />
                                    </span>
                                    <span className="search-hero-benefit-text">Profesionales verificados</span>
                                </div>
                                <div className="search-hero-benefit">
                                    <span className="search-hero-benefit-icon">
                                        <FaRegClock />
                                    </span>
                                    <span className="search-hero-benefit-text">Respuestas en minutos</span>
                                </div>
                                <div className="search-hero-benefit">
                                    <span className="search-hero-benefit-icon">
                                        <FaUserCheck />
                                    </span>
                                    <span className="search-hero-benefit-text">Evaluaciones transparentes</span>
                                </div>
                            </div>
                            <div className="search-shortcut-pills" role="list">
                                {quickPrompts.map(prompt => (
                                    <button
                                        key={prompt.id}
                                        type="button"
                                        className="search-shortcut-pill"
                                        onClick={() => handleQuickPrompt(prompt)}
                                    >
                                        <span className="search-shortcut-pill-icon">{prompt.icon}</span>
                                        {prompt.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="search-hero-card">
                            <InputAdvanced
                                field={{
                                    name: 'googleAddressAutocompleteTrigger',
                                    label: 'Ingresa tu dirección',
                                    description: 'Ej. Av. Primavera 123, Santiago de Surco',
                                }}
                                value={addressValue}
                                onInputChange={handleAddressInputChange}
                                onPlaceSelected={handlePlaceSelected}
                                hasError={Boolean(addressError)}
                                errorMessage={addressError || undefined}
                                startContent={<FaMapMarkerAlt />}
                                endContent={<Button variant="ghost" size="sm" onClick={openLocationSetup}>Editar</Button>}
                            />

                            <div className="search-location-actions">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleManualDistrict(addressValue)}
                                >
                                    Usar este distrito
                                </Button>
                                <Button variant="primary" size="sm" onClick={openLocationSetup}>
                                    Elegir manualmente
                                </Button> 
                            </div>

                            <div className="search-location-caption">
                                {location?.districtName
                                    ? `Filtrando resultados en ${location.districtName}`
                                    : 'Selecciona un distrito para mostrar proveedores cercanos'}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="search-container">
                <div className="search-toolbar">
                    <div className="search-toolbar-input">
                        <Input
                            name="search"
                            type="search"
                            placeholder="Buscar servicios o proveedores"
                            value={searchQuery}
                            setValue={setSearchQuery}
                            startContent={<FaSearch />}
                            fullWidth
                        />
                    </div>
                    <div className={`search-toolbar-meta ${showLocationWarning ? 'search-toolbar-meta--pending' : ''}`}>
                        <div className="search-toolbar-count">
                            <strong>
                                {!location?.districtId 
                                    ? (allProvidersFeed?.total_count || 0)
                                    : (districtFeed?.total_count || 0) + (allProvidersFeed?.total_count || 0)
                                }
                            </strong>
                            <span>servicios disponibles</span>
                        </div>
                        <div className="search-toolbar-location">
                            <span className="search-toolbar-location-label">Zona activa</span>
                            <span className="search-toolbar-location-value">
                                {location?.districtName || 'Lima Metropolitana'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="search-filters-row">
                    <section onClick={() => setIsCategoryExpanded(!isCategoryExpanded)} className={`search-filter-card ${isCategoryExpanded ? 'search-filter-card--expanded' : ''}`}>
                        <div className="search-filter-card-header">
                            <div className="search-filter-header">
                                <span className="search-filter-eyebrow">Filtrar por categoría</span>
                                {categoryIds.length > 0 && (
                                    <span className="search-filter-badge">{categoryIds.length} seleccionada{categoryIds.length > 1 ? 's' : ''}</span>
                                )}
                            </div>
                            {isCategoryExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>

                        <div className={`search-filter-content ${isCategoryExpanded ? 'search-filter-content--expanded' : ''}`}>
                            <div className="search-filter-header">
                                {/* <span className="search-filter-eyebrow">Categorías principales</span> */}
                                <div className="search-filter-title">
                                    <h2>Explora servicios profesionales</h2>
                                    <p>Activa una o más categorías para descubrir especialistas verificados.</p>
                                </div>
                            </div>

                            <div className="search-categories-grid">
                            <button
                                    type="button"
                                    className={`search-category-chip ${categoryIds.length === 0 ? 'search-category-chip--active' : ''}`}
                                    onClick={() => handleCategoryToggle(null)}
                            >
                                <span className="search-category-text">Todas</span>
                            </button>
                            {(categories || []).map((c: any) => {
                                const formattedName = c.name
                                    .toLowerCase()
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, (letter: string) => letter.toUpperCase())

                                return (
                                    <button
                                        key={c.id}
                                            type="button"
                                            className={`search-category-chip ${categoryIds.includes(c.id) ? 'search-category-chip--active' : ''}`}
                                            onClick={() => handleCategoryToggle(c.id)}
                                    >
                                        <span className="search-category-text">{formattedName}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    </section>

                    <section onClick={() => setIsPricingExpanded(!isPricingExpanded)} className={`search-filter-card ${isPricingExpanded ? 'search-filter-card--expanded' : ''}`}>
                        <div className="search-filter-card-header">
                            <div className="search-filter-header">
                                <span className="search-filter-eyebrow">Filtrar por tipo de servicio</span>
                                {pricingFilters.length > 0 && (
                                    <span className="search-filter-badge">{pricingFilters.length} seleccionado{pricingFilters.length > 1 ? 's' : ''}</span>
                                )}
                            </div>
                            {isPricingExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>

                        <div className={`search-filter-content ${isPricingExpanded ? 'search-filter-content--expanded' : ''}`}>
                            <div className="search-filter-header">
                                {/* <span className="search-filter-eyebrow">Tipo de servicio</span> */}
                                <div className="search-filter-title">
                                    <h2>Define cómo deseas contratar</h2>
                                    <p>Selecciona uno o más modelos que se adapten a tu necesidad y presupuesto.</p>
                                </div>
                            </div>

                        <div className="search-pricing-filters">
                            <button
                                    type="button"
                                    data-caption="Visualiza todas las modalidades disponibles"
                                    className={`search-pricing-chip ${pricingFilters.length === 0 ? 'search-pricing-chip--active' : ''}`}
                                    onClick={() => handlePricingToggle('all')}
                            >
                                Todos
                            </button>
                            <button
                                    type="button"
                                    data-caption="Profesionales que cobran por hora de trabajo"
                                    className={`search-pricing-chip ${pricingFilters.includes('hourly') ? 'search-pricing-chip--active' : ''}`}
                                    onClick={() => handlePricingToggle('hourly')}
                            >
                                Cobro por hora
                            </button>
                            <button
                                    type="button"
                                    data-caption="Recibe una propuesta personalizada"
                                    className={`search-pricing-chip ${pricingFilters.includes('quote') ? 'search-pricing-chip--active' : ''}`}
                                    onClick={() => handlePricingToggle('quote')}
                            >
                                Cotización
                            </button>
                        </div>
                    </div>
                    </section>
                </div>

                <div className="search-results-section">
                    {!location?.districtId ? (
                        // No district selected - show all providers
                        <>
                            <h2 className="search-section-title">Todos los proveedores</h2>
                            {isLoading ? (
                        <div className="search-empty-state">
                            <p>Cargando proveedores...</p>
                        </div>
                    ) : isError ? (
                        <div className="search-empty-state">
                            <p>Error al cargar proveedores</p>
                            <Button variant="secondary" size="md" onClick={() => refetch()}>
                                Reintentar
                            </Button>
                        </div>
                            ) : filteredAllProviders.length === 0 ? (
                        <div className="search-empty-state">
                            <p>No encontramos proveedores con estos filtros.</p>
                                    <Button variant="primary" size="md" onClick={resetFilters}>
                                        Restablecer filtros
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="search-providers-grid">
                                        {filteredAllProviders.map((provider: any) => (
                                            <ProviderCard key={provider.id} provider={provider} />
                                        ))}
                                    </div>
                                    {allProvidersFeed && (
                                        <div className="search-pagination">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setAllProvidersPage(prev => Math.max(1, prev - 1))}
                                                disabled={!allProvidersFeed.has_previous || isLoading}
                                            >
                                                Anterior
                                            </Button>
                                            <span className="search-pagination-info">
                                                Página {allProvidersFeed.page} de {Math.ceil((allProvidersFeed.total_count || 0) / 20)}
                                            </span>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setAllProvidersPage(prev => prev + 1)}
                                                disabled={!allProvidersFeed.has_next || isLoading}
                                            >
                                                Siguiente
                            </Button>
                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        // District selected - show district providers first, then "Other Providers"
                        <>
                            <h2 className="search-section-title">
                                Proveedores en {location.districtName}
                            </h2>
                            {isLoadingDistrict ? (
                                <div className="search-empty-state">
                                    <p>Cargando proveedores...</p>
                                </div>
                            ) : isErrorDistrict ? (
                                <div className="search-empty-state">
                                    <p>Error al cargar proveedores</p>
                                    <Button variant="secondary" size="md" onClick={() => refetch()}>
                                        Reintentar
                                    </Button>
                                </div>
                            ) : filteredDistrictProviders.length === 0 ? (
                                <div className="search-empty-state">
                                    <p>No encontramos proveedores en este distrito con estos filtros.</p>
                                    <Button variant="primary" size="md" onClick={resetFilters}>
                                        Restablecer filtros
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="search-providers-grid">
                                        {filteredDistrictProviders.map((provider: any) => (
                                            <ProviderCard key={provider.id} provider={provider} />
                                        ))}
                                    </div>
                                    {districtFeed && (
                                        <div className="search-pagination">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setDistrictPage(prev => Math.max(1, prev - 1))}
                                                disabled={!districtFeed.has_previous || isLoadingDistrict}
                                            >
                                                Anterior
                                            </Button>
                                            <span className="search-pagination-info">
                                                Página {districtFeed.page} de {Math.ceil((districtFeed.total_count || 0) / 20)}
                                            </span>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setDistrictPage(prev => prev + 1)}
                                                disabled={!districtFeed.has_next || isLoadingDistrict}
                                            >
                                                Siguiente
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Other Providers Section */}
                            <div className="search-results-section" style={{ marginTop: '3rem' }}>
                                <h2 className="search-section-title">Otros proveedores</h2>
                                {isLoadingAll ? (
                                    <div className="search-empty-state">
                                        <p>Cargando proveedores...</p>
                                    </div>
                                ) : isErrorAll ? (
                                    <div className="search-empty-state">
                                        <p>Error al cargar proveedores</p>
                                        <Button variant="secondary" size="md" onClick={() => refetch()}>
                                            Reintentar
                                        </Button>
                                    </div>
                                ) : filteredAllProviders.length === 0 ? (
                                    <div className="search-empty-state">
                                        <p>No hay más proveedores disponibles.</p>
                                    </div>
                                ) : (
                                    <>
                        <div className="search-providers-grid">
                                            {filteredAllProviders.map((provider: any) => (
                                <ProviderCard key={provider.id} provider={provider} />
                            ))}
                        </div>
                                        {allProvidersFeed && (
                                            <div className="search-pagination">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => setAllProvidersPage(prev => Math.max(1, prev - 1))}
                                                    disabled={!allProvidersFeed.has_previous || isLoadingAll}
                                                >
                                                    Anterior
                                                </Button>
                                                <span className="search-pagination-info">
                                                    Página {allProvidersFeed.page} de {Math.ceil((allProvidersFeed.total_count || 0) / 20)}
                                                </span>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => setAllProvidersPage(prev => prev + 1)}
                                                    disabled={!allProvidersFeed.has_next || isLoadingAll}
                                                >
                                                    Siguiente
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ServiceSearch


